import axios from 'axios';
import _ from 'lodash';
import { isValid, parseISO, addMinutes, format } from 'date-fns';
import type { KeyboardEventHandler } from 'react';
import normalizeUrl from 'normalize-url';
import { deviceResolutions } from '../consts';
import config from '../../config';
import type { Size } from '../types';
import type { ModelDefinition, ObjectDefinition, SourceDefinition } from '../types/Schema';
import logger from './logging/logger';

export function filterObjectKeys(obj: { [k: string]: $TSFixMe }, allowedKeys: string[]): { [k: string]: $TSFixMe } {
    return Object.keys(obj)
        .filter((key) => allowedKeys.indexOf(key) !== -1)
        .reduce<{ [k: string]: unknown }>((prev, cur) => {
            prev[cur] = obj[cur];
            return prev;
        }, {});
}

export function delayPromise(delay: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    });
}

export async function importRetry(importFn: () => Promise<$TSFixMe>, retries = 2, interval = 1000): Promise<$TSFixMe> {
    try {
        return await importFn();
    } catch (error) {
        if (retries) {
            await delayPromise(interval);
            return importRetry(importFn, retries - 1, interval);
        }
        throw error;
    }
}

const LAST_VERSION_RELOAD_KEY = 'stackbit.lastVersionReload';
const LAST_VERSION_RELOAD_TIMEOUT = 10000;

export async function importWithValidate(importFn: () => Promise<$TSFixMe>, origin = window.location.origin, reloadFn?: () => void): Promise<$TSFixMe> {
    const currentVersion = process.env.COMMIT_REF;

    if (config.env === 'local') {
        return importFn();
    }

    let response;

    try {
        response = await axios({ method: 'HEAD', url: new URL('/v', origin).toString() });
    } catch (e) {
        return importFn();
    }

    const { version } = response.headers;

    // if we have version both current bundle & latest bundle
    if (typeof version === 'string' && typeof currentVersion === 'string' && currentVersion !== version) {
        logger.info('Bundle version mismatch', { currentVersion, newVersion: version });

        const lastReload = new Date(parseInt(window.sessionStorage.getItem(LAST_VERSION_RELOAD_KEY) ?? '', 10));
        const lastReloadDate = Number.isNaN(lastReload.getTime()) ? undefined : lastReload;
        const isDeadlock = Date.now() - (lastReloadDate?.getTime() ?? 0) < LAST_VERSION_RELOAD_TIMEOUT;

        if (isDeadlock) {
            logger.error('Bundle version reload deadlock', { currentVersion, newVersion: version });
            // continue regular execution, probably will crash but this if-cond eventually should never happen
            return importFn();
        }

        window.sessionStorage.setItem(LAST_VERSION_RELOAD_KEY, Date.now().toString());
        setTimeout(() => {
            // have a chance to send log message
            if (reloadFn) {
                reloadFn();
            } else {
                window.location.reload();
            }
        });

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return new Promise(() => {}); // never resolves
    }

    return importFn();
}

export function getFulldate(date: Date): string {
    let dd: number | string = date.getDate();
    let mm: number | string = date.getMonth() + 1; // January is 0!
    const yyyy = date.getFullYear();

    if (dd < 10) {
        dd = `0${dd}`;
    }

    if (mm < 10) {
        mm = `0${mm}`;
    }

    return `${mm}/${dd}/${yyyy}`;
}

export function getHour(date: Date): string {
    let hours: number | string = date.getHours();
    let minutes: number | string = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours %= 12;
    hours = hours || 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    const strTime = `${hours}:${minutes} ${ampm}`;
    return strTime;
}

export function isMobile(): boolean {
    return matchMedia(`only screen and (max-width: ${deviceResolutions.mobile}px)`).matches;
}

export function isTablet(): boolean {
    return (
        matchMedia(`(min-width: ${deviceResolutions.mobile + 1}px) and (max-width: ${deviceResolutions.tablet}px)`).matches ||
        matchMedia('all and (orientation:landscape)').matches
    );
}

export function inRange(number: number, opts: { start?: number; end: number }): boolean {
    const { start = 0, end } = opts;
    return number >= Math.min(start, end) && number <= Math.max(end, start);
}

export const isShallowEqual = (v: $TSFixMe, o: $TSFixMe): boolean => {
    let key;

    for (key in v) {
        if (!(key in o) || v[key] !== o[key]) {
            return false;
        }
    }

    for (key in o) {
        if (!(key in v) || v[key] !== o[key]) {
            return false;
        }
    }

    return true;
};

export type ControlPromise<T> = Promise<T> & {
    resolve: PromiseResolve<T>;
    reject: PromiseReject;
    info: $TSFixMe;
};

export type PromiseResolve<T> = { (value: T | PromiseLike<T>): void };
export type PromiseReject<T = $TSFixMe> = { (error?: T): void };

export const defer = <T>(): ControlPromise<T> => {
    let res: PromiseResolve<T>;
    let rej: PromiseReject;

    const promise = new Promise((resolve, reject) => {
        res = resolve;
        rej = reject;
    }) as ControlPromise<T>;

    promise.resolve = res!;
    promise.reject = rej!;

    return promise;
};

export const deferPrivate = <T>(): [Promise<T>, PromiseResolve<T>, PromiseReject] => {
    let res: PromiseResolve<T>;
    let rej: PromiseReject;

    const promise = new Promise((resolve, reject) => {
        res = resolve;
        rej = reject;
    }) as ControlPromise<T>;

    return [promise, res!, rej!];
};

export function delayedPromise<T>(result: T, timeout: number): Promise<T> {
    return new Promise((resolve) => setTimeout(() => resolve(result), timeout));
}

export function delayedPromiseFromCallback(
    result: { (resolve: { (value: $TSFixMe): void }, reject: { (error: $TSFixMe): void }): void },
    timeout: number
): Promise<void> {
    return new Promise((resolve, reject) => setTimeout(() => result(resolve, reject), timeout));
}

export const getServiceProjectName = (serviceId: string, serviceData: $TSFixMe): string | undefined => {
    const serviceUrl: string = _.get(serviceData, 'url', '');
    const repoName = serviceUrl.replace('https://github.com/', '');

    switch (serviceId) {
        case 'contentful':
            return serviceData.spaceName;
        case 'sanity':
            return serviceData.projectName;
        case 'datocms':
            return serviceUrl.replace('https://', '').split('.')[0] ?? '';
        case 'forestry':
            return serviceData.siteId;
        case 'github':
            return repoName.replace('stackbit-projects/', '');
        case 'bitbucket':
            return repoName;
        case 'netlify':
            return _.last(serviceUrl.split('/')) ?? '';
        case 'digitalocean':
            return serviceData.name;
    }

    return undefined;
};

export const pathJoin = (...paths: string[]): string => {
    const sep = '/';
    const replaceEx = new RegExp(`${sep}{1,}`, 'g');
    return paths.join(sep).replace(replaceEx, sep);
};

export const bindDefaultKeyUpActions =
    (handler: KeyboardEventHandler): KeyboardEventHandler =>
    (e): void => {
        if (e.key === 'Enter') {
            handler(e);
        } else if (e.key === 'Escape') {
            (e.target as HTMLElement).blur();
        }
    };

export const normalizeUrlPath = (url: string, opts?: { leadingSlash?: boolean; trailingSlash?: boolean }): string => {
    const { leadingSlash, trailingSlash } = opts || {};
    const rawUrl = _.trim(url, '/');

    return `${leadingSlash ? '/' : ''}${rawUrl}${trailingSlash ? '/' : ''}`;
};

export const areUrlPathsEqual = (left?: string, right?: string): boolean => {
    if (left === undefined && right === undefined) {
        return true;
    }
    if (left === undefined || right === undefined) {
        return false;
    }
    return normalizeUrl(`https://example.com/${left}`) === normalizeUrl(`https://example.com/${right}`);
};

export const randomSuffixString = (): string => Math.random().toString(36).substr(2, 5);

export const isJSTReferrer = (queryParams: { [k: string]: string }): boolean =>
    _.get(queryParams, 'utm_source') === 'jamstackthemes' && _.get(queryParams, 'utm_campaign') === 'custom_themes';
export const isAzureMarketplaceReferrer = (queryParams: { [k: string]: string }): boolean => _.get(queryParams, 'utm_source') === 'marketplace_app';

export interface NetworkPerformanceAnalytics {
    loadTime?: number;
    size?: number;
}

export const getNetworkPerformanceAnalyticsForEntry = (url?: string): NetworkPerformanceAnalytics => {
    const performance = _.last(window.performance?.getEntriesByName?.(url ?? ''));
    if (!performance) {
        return {};
    }
    return {
        loadTime: Math.ceil(performance.duration),
        size: performance.transferSize
    };
};

export const normalizeCssValue = (value: string | number): string => (typeof value === 'number' ? `${value}px` : value);

export const getNormalizedDate = (isoDateStr: string): Date | null => {
    const isoDate = parseISO(isoDateStr);
    return isValid(isoDate) ? isoDate : null;
};

// https://github.com/date-fns/date-fns/issues/1401#issuecomment-578580199
export const toUtcDate = (date: Date): string => format(addMinutes(date, date.getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss');

export const validateEmail = (email: string): boolean => {
    // eslint-disable-next-line
    const tester = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

    if (!email) {
        return false;
    }

    const emailParts: string[] = email.split('@');

    if (emailParts.length !== 2) {
        return false;
    }

    const account: string = emailParts[0] as string;
    const address: string = emailParts[1] as string;

    if (account.length > 64) {
        return false;
    } else if (address.length > 255) {
        return false;
    }

    const domainParts = address.split('.');
    if (domainParts.some((part) => part.length > 63)) return false;

    if (!tester.test(email)) return false;

    return true;
};

export const capitalize = (text: string): string => (text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '');

export const dataURItoBlob = (dataURI: string): Blob => {
    const dataParts = dataURI.split(',');

    const byteString = atob(dataParts[1]!);
    const mimeString = dataParts[0]!.split(':')[1]!.split(';')[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
};

export const isObject = (item: $TSFixMe): boolean => item && typeof item === 'object' && !Array.isArray(item);

export const mergeDeep = (target: $TSFixMe, ...sources: $TSFixMe[]): $TSFixMe => {
    if (!sources.length) {
        return target;
    }

    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) {
                    Object.assign(target, { [key]: {} });
                }
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return mergeDeep(target, ...sources);
};

export const findMap = <T>(arr: Array<T>, handler: { (el: T, index: number, arr: Array<T>): $TSFixMe }): $TSFixMe => {
    let seek;
    arr.some((el, index, arr) => {
        const res = handler(el, index, arr);
        if (res) {
            seek = res;
            return true;
        }
        return false;
    });
    return seek;
};

// add to list when needed
const irregularSingulars = {
    entry: 'entries',
    was: 'were'
};

const irregularPlurals = _.invert(irregularSingulars);

const getPlural = (singular: string, suffix: string): string => irregularSingulars[singular as keyof typeof irregularSingulars] ?? `${singular}${suffix}`;

export const pluralize = (count: number, singular: string, opts: { suffix?: string; useCount?: boolean } = {}) => {
    const suffix = opts.suffix ?? 's';
    const useCount = opts.useCount ?? true;
    const pluralizedStr = count === 1 ? singular : getPlural(singular, suffix);
    return useCount ? `${count} ${pluralizedStr}` : pluralizedStr;
};

export const makeSingular = (str: string) => irregularPlurals[str] ?? str.replace(/s$/, '');

export const getIdForObjectDefinition = (objectDefinition: ObjectDefinition) =>
    `${objectDefinition.srcType}:${objectDefinition.srcProjectId}:${objectDefinition.srcObjectId}`;

export const getIdForModelDefinition = (modelDefinition: ModelDefinition) =>
    `${modelDefinition.srcType}:${modelDefinition.srcProjectId}:${modelDefinition.name}`;

export const getIdForCollectionDefinition = (modelDefinition: SourceDefinition & { modelName: string }) =>
    `${modelDefinition.srcType}:${modelDefinition.srcProjectId}:${modelDefinition.modelName}`;

export const matchSizeInBounds = <T extends Size | Partial<Size>>(current: T, target: T, bounds: { min: T; max: T }) => {
    const result = { ...current };
    (['width', 'height'] as const).forEach((key) => {
        if (typeof target[key] === 'undefined') {
            return;
        }
        if (typeof bounds.min[key] !== 'undefined' && target[key]! < bounds.min[key]!) {
            result[key] = bounds.min[key];
        } else if (typeof bounds.max[key] !== 'undefined' && target[key]! > bounds.max[key]!) {
            result[key] = bounds.max[key];
        } else {
            result[key] = target[key];
        }
    });
    return result;
};
